import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import MenuItem from '@mui/material/MenuItem'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */
import useStyles from 'features/setting/default-products/product-units/style'

/** API */
import ProductUnitsApi from 'api/setting/default-products/product-unit.api'

/** UTILS || SLICE */
import { submitModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextField from 'component/Input/InputTextField'
import InputTextSearch from 'component/Input/InputTextSearch'
import ModalCustom from 'component/ModalCustom/ModalCustom'
import TableCustom from 'component/Table/TableCustom'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import TableRowCommon from 'component/Table/TableRowCommon'
import FilterSelect from 'component/Select/FilterSelect'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import { Box, Typography } from '@mui/material'
import PageModal from 'new-components/modal/page-modal'
import SwitchButton from 'new-components/buttons/switch-button'
import TextInput from 'new-components/inputs/text-input'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  PRODUCT_UNIT_NAME: ``,
  IS_DUPLICATE_PRODUCT_UNIT_NAME: ``,
  PRODUCT_UNIT_NAME_STRING_BASE: ``,
  PRODUCT_UNIT_NAME_STRING_EMPTY: ``,
  PRODUCT_UNIT_NAME_MAXIMUM_LENGTH: ``,
  PRODUCT_UNIT_NAME_ANY_REQUIRED: ``,

  PRODUCT_UNIT_NAME_EN: ``,
  IS_DUPLICATE_PRODUCT_UNIT_NAME_EN: ``,
  PRODUCT_UNIT_NAME_EN_STRING_BASE: ``,
  PRODUCT_UNIT_NAME_EN_STRING_EMPTY: ``,
  PRODUCT_UNIT_NAME_EN_MAXIMUM_LENGTH: ``,
  PRODUCT_UNIT_NAME_EN_ANY_REQUIRED: ``
}
export default function ProductUnits() {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [productUnits, setProductUnits] = useState([])
  const [productUnitId, setProductUnitId] = useState(Number)
  const [productUnitName, setProductUnitName] = useState('')
  const [productUnitNameEn, setProductUnitNameEn] = useState('')
  const [productUnitStatus, setProductUnitStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isOpenPageModal, setIsOpenPageModal] = useState(false)

  const loadData = useCallback(async () => {

    let condition: any = { group: 'PRODUCT' }
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await ProductUnitsApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setProductUnits(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const submit = () => {
    if (!productUnitName) return setErrorMessage({ ...errorMessage, ...{ PRODUCT_UNIT_NAME: t('PRODUCT_UNIT.MESSAGE.PRODUCT_UNIT_NAME') } })

    dispatch(submitModal())

    ProductUnitsApi.create({ productUnitName, productUnitNameEn, group: 'PRODUCT' })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('PRODUCT_UNIT.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          setIsOpenPageModal(false)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_UNIT.MESSAGE.${err.message}`) } })
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_UNIT.MESSAGE.${err.message}`) } })
      })
  }

  const submitEdit = () => {
    if (!productUnitName) return setErrorMessage({ ...errorMessage, ...{ PRODUCT_UNIT_NAME: t(`PRODUCT_UNIT.MESSAGE.PRODUCT_UNIT_NAME`) } })

    dispatch(submitModal())
    ProductUnitsApi.update(productUnitId, { productUnitName, productUnitNameEn, status: productUnitStatus, group: 'PRODUCT' })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`PRODUCT_UNIT.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          setIsOpenPageModal(false)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_UNIT.MESSAGE.${err.message}`) } })
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_UNIT.MESSAGE.${err.message}`) } })
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setProductUnitId(0)
    setProductUnitName('')
    setProductUnitNameEn('')
    setProductUnitStatus('')
    clearErrorMessage()
    setIsOpenPageModal(false)
  }

  const onActive = () => {
    if (id) {
      ProductUnitsApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('PRODUCT_UNIT.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('PRODUCT_UNIT.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      ProductUnitsApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('PRODUCT_UNIT.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('PRODUCT_UNIT.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { productUnitId: pdUnitId, productUnitName: pdUnitName, productUnitNameEn: pdUnitNameEn, status: pdUnitStatus } = data
    setProductUnitId(pdUnitId)
    setProductUnitName(pdUnitName)
    setProductUnitNameEn(pdUnitNameEn)
    setProductUnitStatus(pdUnitStatus)
    setIsOpenPageModal(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'productUnitName', disablePadding: false, label: t('PRODUCT_UNIT.TABLE.CELL.PRODUCT_UNIT_NAME') },
    { id: 'productUnitNameEn', disablePadding: false, label: t('PRODUCT_UNIT.TABLE.CELL.PRODUCT_UNIT_NAME_EN') },
    { id: 'status', disablePadding: false, label: t('PRODUCT_UNIT.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('PRODUCT_UNIT.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { productUnitId: pdUnitId, productUnitName: pdUnitName, productUnitNameEn: pdUnitNameEn, updatedBy, updatedAt, status: pdUnitStatus } = objData
    const statusBtnActive = pdUnitStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = pdUnitStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: pdUnitId,
      id: pdUnitId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: pdUnitName },
        { option: 'TEXT', align: 'left', label: pdUnitNameEn },
        { option: 'STATUS', align: 'center', label: pdUnitStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.productUnitName)
    setNameEn(obj.productUnitNameEn)
  }
  return (
    <div className={classes.root}>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`PRODUCT_UNIT.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`PRODUCT_UNIT.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { productUnitName: name, productUnitNameEn: nameEn && `(${nameEn})`, status: t(`PRODUCT_UNIT.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <PageModal
        isOpen={isOpenPageModal}
        title={productUnitId ? t('PRODUCT_UNIT.TITLE_UPDATE') : t('PRODUCT_UNIT.TITLE_CREATE')}
        size='XS'
        handleClose={resetForm}
        onCancel={{ onClick: resetForm, title: t('PRODUCT_UNIT.BUTTON.CANCEL') }}
        onSubmit={{ onClick: productUnitId ? submitEdit : submit, title: t('PRODUCT_UNIT.BUTTON.SAVE') }}
      >
        <Box display="flex" flexDirection="column" gap="24px">
          {productUnitId ? <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
            <SwitchButton onChange={() => setProductUnitStatus(productUnitStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={productUnitStatus === 'ACTIVE' ? true : false} />
          </Box> : ''}
          <TextInput
            onChange={(event) => {
              setProductUnitName(event.target.value)
              clearErrorMessage()
            }}
            value={productUnitName}
            label={t('PRODUCT_UNIT.INPUT.PRODUCT_UNIT_NAME')}
            helperText={
              errorMessage.PRODUCT_UNIT_NAME ||
              errorMessage.IS_DUPLICATE_PRODUCT_UNIT_NAME ||
              errorMessage.PRODUCT_UNIT_NAME_STRING_BASE ||
              errorMessage.PRODUCT_UNIT_NAME_STRING_EMPTY ||
              errorMessage.PRODUCT_UNIT_NAME_MAXIMUM_LENGTH ||
              errorMessage.PRODUCT_UNIT_NAME_ANY_REQUIRED
            }
            required
          />
          <TextInput
            onChange={(event) => {
              setProductUnitNameEn(event.target.value)
              clearErrorMessage()
            }}
            value={productUnitNameEn}
            helperText={
              errorMessage.PRODUCT_UNIT_NAME_EN ||
              errorMessage.IS_DUPLICATE_PRODUCT_UNIT_NAME_EN ||
              errorMessage.PRODUCT_UNIT_NAME_EN_STRING_BASE ||
              errorMessage.PRODUCT_UNIT_NAME_EN_STRING_EMPTY ||
              errorMessage.PRODUCT_UNIT_NAME_EN_MAXIMUM_LENGTH ||
              errorMessage.PRODUCT_UNIT_NAME_EN_ANY_REQUIRED
            }
            label={t('PRODUCT_UNIT.INPUT.PRODUCT_UNIT_NAME_EN')}
          />
        </Box>
      </PageModal>
      <Card className="border-0 h-100">
        <HeaderCard text={t('PRODUCT_UNIT.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row className="align-items-center">
              <Col md={8}>
                <Row className="flax-row">
                  <Col sm={12} md={6} xl={4} className="pt-2">
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('PRODUCT_UNIT.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                  <Col sm={12} md={6} xl={4} className="pl-md-0 pt-2">
                    <FilterSelect
                      onchange={(event) => {
                        const value = event.target.value
                        if (value) {
                          handleChangeStatus(value)
                          setPage(1)
                        }
                      }}
                      renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                      label={t('STATUS.LABEL')}
                      selectId="select-status"
                      value={status}
                      labelId="label-status"
                      options={[
                        <MenuItem key="1" value="ALL">
                          {t('STATUS.ALL')}
                        </MenuItem>,
                        <MenuItem key="2" value="ACTIVE">
                          {t('STATUS.ACTIVE')}
                        </MenuItem>,
                        <MenuItem key="3" value="INACTIVE">
                          {t('STATUS.INACTIVE')}
                        </MenuItem>
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="mt-2 mt-md-auto">
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => setIsOpenPageModal(true)} textButton={t('PRODUCT_UNIT.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={productUnits.map((val, i) => {
                return renderData(val, i)
              })}
            />
          </div>
        </Card.Body>
      </Card>

      <ModalCustom
        title={productUnitId ? t('PRODUCT_UNIT.TITLE_UPDATE') : t('PRODUCT_UNIT.TITLE_CREATE')}
        component={
          <div className="pb-2">
            {productUnitId ? (
              <div>
                <Form className="mx-0 mt-0 mb-3 p-0  d-inline-flex w-100">
                  <Form.Label className=" mr-auto">{t('INPUT.LABEL_STATUS')}</Form.Label>
                  <Form.Check type="switch" id="custom-switch" onChange={() => setProductUnitStatus(productUnitStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={productUnitStatus === 'ACTIVE' ? true : false} style={{ marginLeft: 'auto' }} />
                </Form>
              </div>
            ) : (
              ''
            )}

            <InputTextField
              onchange={(event) => {
                setProductUnitName(event.target.value)
                clearErrorMessage()
              }}
              value={productUnitName}
              label={t('PRODUCT_UNIT.INPUT.PRODUCT_UNIT_NAME')}
              helperText={
                errorMessage.PRODUCT_UNIT_NAME ||
                errorMessage.IS_DUPLICATE_PRODUCT_UNIT_NAME ||
                errorMessage.PRODUCT_UNIT_NAME_STRING_BASE ||
                errorMessage.PRODUCT_UNIT_NAME_STRING_EMPTY ||
                errorMessage.PRODUCT_UNIT_NAME_MAXIMUM_LENGTH ||
                errorMessage.PRODUCT_UNIT_NAME_ANY_REQUIRED
              }
              required={true}
              style={{ marginBottom: '1rem' }}
            />

            <InputTextField
              key="productUnitNameEn"
              onchange={(event) => {
                setProductUnitNameEn(event.target.value)
                clearErrorMessage()
              }}
              value={productUnitNameEn}
              helperText={
                errorMessage.PRODUCT_UNIT_NAME_EN ||
                errorMessage.IS_DUPLICATE_PRODUCT_UNIT_NAME_EN ||
                errorMessage.PRODUCT_UNIT_NAME_EN_STRING_BASE ||
                errorMessage.PRODUCT_UNIT_NAME_EN_STRING_EMPTY ||
                errorMessage.PRODUCT_UNIT_NAME_EN_MAXIMUM_LENGTH ||
                errorMessage.PRODUCT_UNIT_NAME_EN_ANY_REQUIRED
              }
              label={t('PRODUCT_UNIT.INPUT.PRODUCT_UNIT_NAME_EN')}
              required={false}
            />
          </div>
        }
        onReset={resetForm}
        onSubmit={productUnitId ? submitEdit : submit}
        textBtnCancel={t('PRODUCT_UNIT.BUTTON.CANCEL')}
        textBtnConfirm={t('PRODUCT_UNIT.BUTTON.SAVE')}
      />
    </div>
  )
}
